import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import useTrackShareInitiated from 'hooks/useTrackShareInitiated';
import api from 'api';
import { LISTING_SHARE_MODE } from 'models/MixpanelEvents';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import {
  getCheckedValuesInObject,
  getIsArrayEmpty,
  getArrayLength,
} from 'utils';
import { InviteContactsFormValues } from './components/InviteContactsForm/InviteContactsForm.component';
import InviteContactsForm from './components/InviteContactsForm';
import { CONTACTS_ROUTE } from 'data/global';
import { useNavigate } from 'react-router-dom';

import './ShareListingWithContacts.styles.scss';

type ShareListingWithContactsProps = {
  className?: string;
  dealRoomId: string;
  dealRoomName?: string;
  listingId: string;
  listingName: string;
  onBack: () => void;
} & ModalProps;

const ShareListingWithContacts: React.ForwardRefRenderFunction<
  ModalRef,
  ShareListingWithContactsProps
> = (props, ref) => {
  const {
    className,
    listingId,
    listingName,
    dealRoomId,
    dealRoomName,
    onBack,
    ...rest
  } = props;

  const [hasContacts, setHasContatcs] = useState(true);
  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { popSuccess } = useContext(PopNotificationsContext);

  const classes = classNames('share-listing-with-contacts', className);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const trackShareInitiated = useTrackShareInitiated({ listingId, listingName });

  const onClose = useCallback(() => modal?.close(), [modal]);

  const onSubmit = useCallback(
    async (values: InviteContactsFormValues) => {
      const { contacts, includeMessage, customMessage } = values;

      const contactsIds = getCheckedValuesInObject(contacts);

      if (getIsArrayEmpty(contactsIds)) return;

      const peopleCount = getArrayLength(contactsIds);

      trackShareInitiated(LISTING_SHARE_MODE.CONTACTS, peopleCount);

      await api.portfolio.seller.shareListingWithContacts(listingId, {
        contactsIds,
        ...(includeMessage && customMessage && { customMessage }),
      });

      popSuccess(t('listingSharedWithContacts', { count: peopleCount }));
      onClose();
    },
    [listingId, trackShareInitiated, onClose, popSuccess, t],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t(
        hasContacts ? 'shareListingWithContacts' : 'noContactsAvailable',
      )}
      {...rest}
    >
      <p>
        {t(
          hasContacts ? 'selectContactsToShareDeal' : 'youDontHaveContactsYes',
          {
            dealRoomName: dealRoomName || t('thisDealRoom'),
          },
        )}
      </p>

      {hasContacts ? (
        <InviteContactsForm
          submitText={t('shareListing')}
          dealRoomId={dealRoomId}
          onSubmit={onSubmit}
          onClose={onClose}
          updateIsEmpty={(value) => setHasContatcs(!value)}
        />
      ) : (
        <div className="actions">
          <Button variant="link" onClick={onBack}>
            {t('goBack')}
          </Button>
          <Button variant="outline" onClick={() => navigate(CONTACTS_ROUTE)}>
            {t('manageContacts')}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default forwardRef(ShareListingWithContacts);
